/** @format */

import Skeleton from '@mui/material/Skeleton';
import InnerHTML from 'dangerously-set-html-content';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { GA4_DOCUMENT_CATEGORY } from 'src/analytics';
import { buildDocumentDownloadParams } from 'src/analytics/buildGa4EventPayload';
import { DownloadIcon, ViewIcon } from 'src/components/icons';
import useGa4Analytics from 'src/hooks/useGa4Analytics';
import { prependApiUrlToCmsPaths } from 'src/utils/cmsContent';
import { formatDate } from 'src/utils/helper';
import { buildSearchUrlSearch } from 'src/utils/searchUrl';
import PDFViewer from 'src/views/extranet/PDFViewer';

const skeletonStyle = { backgroundColor: 'rgba(0, 0, 0, 0.1)' };

function toArray(value) {
  return Array.isArray(value) ? value : [];
}

function normalizeDownloads(detail) {
  if (!detail || typeof detail !== 'object') return [];

  const candidates = [
    ...toArray(detail.downloads),
    ...toArray(detail.attachments),
    ...toArray(detail.files),
    ...toArray(detail.documents)
  ];

  return candidates
    .map(item => {
      if (!item || typeof item !== 'object') return null;
      const url = item.link || item.url || item.href || item.downloadUrl;
      const rawName = item.linkText || item.title || item.name || item.filename || item.fileName || 'File';
      const name = decodeFileName(rawName);
      const size = item.size || item.fileSize || null;
      return url ? { url, name, size, mimeType: item.mimeType } : null;
    })
    .filter(Boolean);
}

function decodeFileName(value) {
  if (value == null) return 'File';
  const text = String(value).trim();
  if (!text) return 'File';
  try {
    return decodeURIComponent(text);
  } catch {
    return text;
  }
}

function splitIntroHtml(html) {
  if (!html || typeof html !== 'string') return { introHtml: '', bodyHtml: '' };
  if (typeof window === 'undefined' || typeof window.DOMParser === 'undefined') {
    return { introHtml: html, bodyHtml: '' };
  }

  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const body = doc.body;
    const nodes = Array.from(body?.childNodes || []).filter(n => {
      if (!n) return false;
      if (n.nodeType === Node.TEXT_NODE) return (n.textContent || '').trim().length > 0;
      if (n.nodeType === Node.ELEMENT_NODE) return true;
      return false;
    });

    const introNode = nodes[0];
    if (!introNode) return { introHtml: '', bodyHtml: '' };

    const wrap = node => {
      if (node.nodeType === Node.TEXT_NODE) return `<p>${node.textContent || ''}</p>`;
      if (node.nodeType === Node.ELEMENT_NODE) return node.outerHTML || '';
      return '';
    };

    const introHtml = wrap(introNode);
    const restNodes = nodes.slice(1).map(wrap).join('');
    return { introHtml, bodyHtml: restNodes };
  } catch {
    return { introHtml: html, bodyHtml: '' };
  }
}

function FileSize({ bytes }) {
  if (!bytes || typeof bytes !== 'number') return null;
  const kb = bytes / 1024;
  if (kb < 1024) return <span>{kb.toFixed(2)} KB</span>;
  const mb = kb / 1024;
  return <span>{mb.toFixed(2)} MB</span>;
}

function mimeToFileLabel(mimeType) {
  if (!mimeType || typeof mimeType !== 'string') return 'FILE';
  const m = mimeType.toLowerCase();
  if (m.includes('pdf')) return 'PDF';
  if (m.includes('word') || m.includes('document')) return 'DOC';
  if (m.includes('sheet') || m.includes('excel')) return 'XLS';
  if (m.includes('presentation') || m.includes('powerpoint')) return 'PPT';
  if (m.includes('text/plain')) return 'TXT';
  return 'FILE';
}

function documentToDownloadFile(doc) {
  if (!doc || typeof doc !== 'object') return null;
  const url = doc.link || doc.url || doc.href || doc.downloadUrl;
  const rawName = doc.linkText || doc.title || doc.name || doc.filename || doc.fileName || 'File';
  const name = decodeFileName(rawName);
  const size = doc.size || doc.fileSize || null;
  return url ? { url, name, size, mimeType: doc.mimeType } : null;
}

function isPdfDocument(file) {
  if (!file || typeof file !== 'object') return false;
  const fileName = String(file.name || '').toLowerCase();
  const mimeType = String(file.mimeType || '').toLowerCase();
  return mimeType.includes('application/pdf') || fileName.endsWith('.pdf');
}

function DownloadFileCard({ file, onPreview }) {
  const { trackDocumentDownload } = useGa4Analytics();

  if (!file?.url) return null;
  const typeLabel = mimeToFileLabel(file.mimeType);
  const canPreviewPdf = isPdfDocument(file);

  const handleDownloadClick = () => {
    trackDocumentDownload(
      buildDocumentDownloadParams({
        document_name: file.name,
        url: file.url,
        document_category: GA4_DOCUMENT_CATEGORY.EXTRANET
      })
    );
  };

  return (
    <div className="topics-content-detail__download-card">
      <div className="topics-content-detail__download-type">{typeLabel}</div>
      <div className="topics-content-detail__download-name">{file.name}</div>
      <div className="topics-content-detail__download-actions">
        {canPreviewPdf ? (
          <button type="button" className="topics-content-detail__icon-btn" onClick={() => onPreview(file)}>
            {ViewIcon ? <ViewIcon /> : null}
          </button>
        ) : null}
        <a
          className="topics-content-detail__icon-btn"
          href={file.url}
          download={file.name}
          target="_blank"
          rel="noreferrer"
          onClick={handleDownloadClick}>
          {DownloadIcon ? <DownloadIcon /> : null}
        </a>
      </div>
      <div className="topics-content-detail__download-size">
        <FileSize bytes={file.size} />
      </div>
    </div>
  );
}

function DownloadCardSkeleton() {
  return (
    <div className="topics-content-detail__download-card topics-content-detail__download-card--skeleton" aria-hidden>
      <Skeleton variant="text" width={34} height={18} style={skeletonStyle} />
      <Skeleton variant="text" width="90%" height={20} style={skeletonStyle} />
      <div className="topics-content-detail__download-actions">
        <Skeleton variant="rectangular" width={32} height={32} style={skeletonStyle} />
        <Skeleton variant="rectangular" width={32} height={32} style={skeletonStyle} />
      </div>
      <Skeleton variant="text" width={64} height={18} style={skeletonStyle} />
    </div>
  );
}

function DownloadCardsSkeleton({ count = 3 }) {
  return (
    <div className="topics-content-detail__downloads" aria-busy="true">
      {Array.from({ length: count }).map((_, index) => (
        <DownloadCardSkeleton key={index} />
      ))}
    </div>
  );
}

/**
 * Presentational article body for topic content detail and preview (no page chrome).
 */
function ContentPartBlock({ part, labels, onPreview }) {
  if (!part || typeof part !== 'object') return null;
  const type = part.type;

  if (type === 'content' && typeof part.text === 'string' && part.text.trim()) {
    const normalizedHtml = prependApiUrlToCmsPaths(part.text.trim());
    return (
      <div className="topics-content-detail__part topics-content-detail__part--content">
        <div className="topics-content-detail__body">
          <InnerHTML html={normalizedHtml} />
        </div>
      </div>
    );
  }

  if (type === 'documents' && Array.isArray(part.documents) && part.documents.length > 0) {
    const files = part.documents.map(documentToDownloadFile).filter(Boolean);
    if (!files.length) return null;
    return (
      <div className="topics-content-detail__part topics-content-detail__part--documents">
        <div className="topics-content-detail__downloads topics-content-detail__downloads--inline">
          {files.map(file => (
            <DownloadFileCard key={file.url} file={file} onPreview={onPreview} />
          ))}
        </div>
      </div>
    );
  }

  if (type === 'imageText') {
    const img = part.image && typeof part.image === 'object' ? part.image : null;
    const imgUrl = img?.url;
    const textHtml = typeof part.text === 'string' ? prependApiUrlToCmsPaths(part.text.trim()) : '';
    const blockTitle = typeof part.title === 'string' ? part.title.trim() : '';
    if (!imgUrl && !textHtml && !blockTitle) return null;
    return (
      <div className="topics-content-detail__part topics-content-detail__part--image-text">
        {imgUrl ? (
          <img
            className="topics-content-detail__image topics-content-detail__part-image"
            src={imgUrl}
            alt={img?.alt || ''}
            loading="lazy"
          />
        ) : null}
        <div className="topics-content-detail__part-content">
          {blockTitle ? <h3 className="topics-content-detail__part-title">{blockTitle}</h3> : null}
          {textHtml ? (
            <div className="topics-content-detail__body">
              <InnerHTML html={textHtml} />
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  if (type === 'salesInformationNo' && part.salesInformationNo != null && String(part.salesInformationNo).trim()) {
    return null; // Disabled for now
    // return (
    //   <div className="topics-content-detail__part topics-content-detail__part--info">
    //     <div className="topics-content-detail__info-label">{labels.salesInformationNo}</div>
    //     <div className="topics-content-detail__info-value">{String(part.salesInformationNo).trim()}</div>
    //   </div>
    // );
  }

  if (type === 'serviceInformationNo' && part.siNumber != null && String(part.siNumber).trim()) {
    return null; // Disabled for now
    // return (
    //   <div className="topics-content-detail__part topics-content-detail__part--info">
    //     <div className="topics-content-detail__info-label">{labels.serviceInformationNo}</div>
    //     <div className="topics-content-detail__info-value">{String(part.siNumber).trim()}</div>
    //   </div>
    // );
  }

  if (type === 'video') {
    const video = part.video && typeof part.video === 'object' ? part.video : null;
    const src = video?.url;
    if (!src) return null;
    return (
      <div className="topics-content-detail__part topics-content-detail__part--video">
        <video className="topics-content-detail__part-video" src={src} controls playsInline />
      </div>
    );
  }

  if (type === 'image') {
    const img = part.image && typeof part.image === 'object' ? part.image : null;
    const imgUrl = img?.url;
    if (!imgUrl) return null;
    return (
      <div className="topics-content-detail__part topics-content-detail__part--image-only">
        <img
          className="topics-content-detail__image topics-content-detail__part-image"
          src={imgUrl}
          alt={img?.alt || ''}
          loading="lazy"
        />
      </div>
    );
  }

  if (type === 'casaNumber') {
    return null; // Disabled for now
    // const parts = toArray(part.casaPartNumbers).filter(n => n != null && String(n).trim());
    // const hierarchies = toArray(part.casaHierarchies).filter(n => n != null && String(n).trim());
    // if (!parts.length && !hierarchies.length) return null;
    // return (
    //   <div className="topics-content-detail__part topics-content-detail__part--casa">
    //     <div className="topics-content-detail__info-label">{labels.casaNumber}</div>
    //     {parts.length ? (
    //       <>
    //         <div className="topics-content-detail__info-label">CASA Part</div>
    //         <ul className="topics-content-detail__info-list">
    //           {parts.map((n, i) => (
    //             <li key={`p-${i}`}>{String(n).trim()}</li>
    //           ))}
    //         </ul>
    //       </>
    //     ) : null}
    //     {hierarchies.length ? (
    //       <>
    //         <div className="topics-content-detail__info-label">CASA Hierarchy</div>
    //         <ul className="topics-content-detail__info-list">
    //           {hierarchies.map((n, i) => (
    //             <li key={`h-${i}`}>{String(n).trim()}</li>
    //           ))}
    //         </ul>
    //       </>
    //     ) : null}
    //   </div>
    // );
  }

  return null;
}

export default function ExtranetContentBody({ detail, showSkeleton = false, isLoadingOverlay = false }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  // Detect if we're in administration context
  const isAdministrationContext = useMemo(() => {
    const pathname = location?.pathname || '';
    return pathname.startsWith('/administration');
  }, [location?.pathname]);
  const partLabels = useMemo(
    () => ({
      salesInformationNo: t('contentEditor.blockTypes.salesInformationNo'),
      serviceInformationNo: t('contentEditor.blockTypes.serviceInformationNo'),
      casaNumber: t('contentEditor.blockTypes.casaNumber')
    }),
    [t]
  );

  const title = detail?.title || '';
  const chips = useMemo(() => {
    const tagChips = toArray(detail?.tags)
      .map(tagItem => (typeof tagItem === 'string' ? tagItem : tagItem?.name))
      .filter(Boolean)
      .map(label => ({ key: `tag-${label}`, label, isTopic: false, topicId: null }));

    const topicChips = toArray(detail?.topics)
      .map(topicItem => {
        if (typeof topicItem === 'string') {
          return { key: `topic-${topicItem}`, label: topicItem, isTopic: true, topicId: null };
        }
        const label = topicItem?.displayName || topicItem?.name;
        if (!label) return null;
        const topicId = topicItem?.id != null ? String(topicItem.id) : null;
        return {
          key: `topic-${topicId || label}`,
          label,
          isTopic: true,
          topicId
        };
      })
      .filter(Boolean);

    const deduped = [];
    const seen = new Set();
    [...topicChips, ...tagChips].forEach(chip => {
      const uniqueKey = `${chip.isTopic ? 'topic' : 'tag'}-${chip.topicId || chip.label}`;
      if (seen.has(uniqueKey)) return;
      seen.add(uniqueKey);
      deduped.push(chip);
    });
    return deduped;
  }, [detail]);
  const publishDate = formatDate(
    detail?.releaseDate || detail?.publishingDate || detail?.publishedAt,
    isAdministrationContext,
    i18n.language
  );
  const downloads = useMemo(() => normalizeDownloads(detail), [detail]);
  const { introHtml, bodyHtml } = useMemo(() => {
    const normalizedHtml = prependApiUrlToCmsPaths(detail?.text || '');
    return splitIntroHtml(normalizedHtml);
  }, [detail?.text]);
  const contentParts = toArray(detail?.contentParts);
  const useContentPartsFlow = contentParts.length > 0;
  const { trackDocumentDownload } = useGa4Analytics();
  const [viewerOpen, setViewerOpen] = useState(false);
  const [currentDoc, setCurrentDoc] = useState(null);

  const toggleViewer = () => {
    setViewerOpen(prev => !prev);
  };

  const handleOpenPreview = file => {
    setCurrentDoc({
      title: file.name,
      size: file.size || 0,
      url: file.url,
      mimeType: file.mimeType || 'application/pdf'
    });
    setViewerOpen(true);
  };

  const handleDownloadFile = doc => {
    if (!doc?.url) return;
    trackDocumentDownload(
      buildDocumentDownloadParams({
        document_name: doc.title || doc.name,
        url: doc.url,
        document_category: GA4_DOCUMENT_CATEGORY.EXTRANET
      })
    );
    window.open(doc.url, '_blank', 'noopener,noreferrer');
  };

  const handleTagNavigate = (tag, event) => {
    event.stopPropagation();
    if (!tag) return;

    const searchParams = new URLSearchParams(location.search);
    const existingKeywords = searchParams.getAll('keywords[]');

    if (!existingKeywords.includes(tag)) {
      existingKeywords.push(tag);
    }

    navigate(`/search${buildSearchUrlSearch({ keywords: existingKeywords })}`);
  };

  if (showSkeleton) {
    return (
      <div className="topics-content-detail__skeleton">
        <h1 className="topics-content-detail__title">
          <Skeleton variant="text" width="70%" height={44} style={skeletonStyle} />
        </h1>
        <div className="topics-content-detail__tags" aria-label={t('extranet.tagsAria')}>
          <Skeleton variant="rectangular" width={80} height={24} style={skeletonStyle} />
          <Skeleton variant="rectangular" width={130} height={24} style={skeletonStyle} />
          <Skeleton variant="rectangular" width={110} height={24} style={skeletonStyle} />
        </div>
        <div className="topics-content-detail__hero">
          <Skeleton variant="rectangular" width="100%" height={260} style={skeletonStyle} />
        </div>
        <div className="topics-content-detail__body">
          <Skeleton variant="text" width="90%" height={28} style={skeletonStyle} />
          <Skeleton variant="text" width="84%" height={28} style={skeletonStyle} />
        </div>
        <div className="topics-content-detail__meta">
          <Skeleton variant="text" width={100} height={24} style={skeletonStyle} />
        </div>
        <DownloadCardsSkeleton />
      </div>
    );
  }

  return (
    <div
      className={`topics-content-detail__content${isLoadingOverlay ? ' topics-content-detail__content--loading' : ''}`}>
      <h1 className="topics-content-detail__title">{title}</h1>

      {chips.length ? (
        <div className="topics-content-detail__tags" aria-label={t('extranet.tagsAria')}>
          {chips.map(chip => (
            <span
              className={`topics-content-detail__tag${chip.isTopic && chip.topicId ? ' hasLink' : ''}`}
              key={chip.key}>
              {chip.isTopic && chip.topicId ? (
                <Link to={`/extranet/${chip.topicId}`}>{chip.label}</Link>
              ) : (
                <button
                  type="button"
                  onClick={event => handleTagNavigate(chip.label, event)}
                  style={{ all: 'unset', cursor: 'pointer', display: 'inline' }}>
                  {chip.label}
                </button>
              )}
            </span>
          ))}
        </div>
      ) : null}

      {useContentPartsFlow ? (
        <>
          {detail?.pictureUrl ? (
            <div className="topics-content-detail__hero topics-content-detail__hero--lead-image">
              <div className="topics-content-detail__hero-media">
                <img className="topics-content-detail__image" src={detail.pictureUrl} alt="" loading="lazy" />
              </div>
            </div>
          ) : null}
          <div className="topics-content-detail__parts">
            {contentParts.map((part, index) => (
              <ContentPartBlock
                key={part?.id != null ? String(part.id) : `part-${index}`}
                part={part}
                labels={partLabels}
                onPreview={handleOpenPreview}
              />
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="topics-content-detail__hero">
            <div className="topics-content-detail__hero-media">
              {detail?.pictureUrl && (
                <img className="topics-content-detail__image" src={detail.pictureUrl} alt="" loading="lazy" />
              )}
            </div>
            {introHtml ? (
              <div className="topics-content-detail__text">
                <InnerHTML html={introHtml} />
              </div>
            ) : null}
          </div>

          {bodyHtml ? (
            <div className="topics-content-detail__body">
              <InnerHTML html={bodyHtml} />
            </div>
          ) : null}
        </>
      )}

      {publishDate ? (
        <div className="topics-content-detail__meta">
          <div className="topics-content-detail__meta-value">{publishDate}</div>
        </div>
      ) : null}

      {!useContentPartsFlow && downloads.length ? (
        <div className="topics-content-detail__downloads">
          {downloads.map(file => (
            <DownloadFileCard key={file.url} file={file} onPreview={handleOpenPreview} />
          ))}
        </div>
      ) : null}
      {!useContentPartsFlow && !downloads.length && isLoadingOverlay ? <DownloadCardsSkeleton /> : null}
      <PDFViewer
        openState={viewerOpen}
        toggleDrawer={toggleViewer}
        currentDoc={currentDoc}
        downloadFile={handleDownloadFile}
      />
    </div>
  );
}
